import {
  FieldWrapper,
  FormattedString,
  FormContext,
  SimpleBuilder,
  SimpleBuilderProps,
  cidrValid,
  getOverlapCidrs,
} from "o4a-react";
import React from "react";
import * as Messages from "../../codegen/Messages";
import { networkOverviewUrl } from "../../constants/docConstants";
import { BaseOciCidrProps, OciCidr } from "./OciCidr";
import { maxNetworkLinkCidrPrefix, minCidrPrefix } from "../../helpers/resourceHelper";

export const enum Fields {
  Address = "Address",
}

export type BaseOciCidrBuilderProps = Omit<
  SimpleBuilderProps,
  "children"
> & BaseOciCidrProps;

export interface OciCidrBuilderProps extends BaseOciCidrBuilderProps {
  disabled?: boolean;
  groupName?: string;
  label?: string;
  required?: boolean;
}

export const OciCidrBuilder = ({
  defaultNumberOfRows,
  defaultValue,
  disabled,
  excludeCidrs,
  fieldName,
  groupName,
  label,
  maxPrefix = maxNetworkLinkCidrPrefix,
  maxRows,
  minPrefix = minCidrPrefix,
  minRows,
  reservedCidrs = [],
  required,
  validator,
}: OciCidrBuilderProps): JSX.Element => {
  const form = React.useContext(FormContext);

  const overlappingAddressesValidator = (value: string): string[] | undefined => {
    const errors: string[] = [];

    if (value && cidrValid(value)) {
      const values = form?.getValuesByFieldPrefix(`${Fields.Address}-`, groupName);
      const cidrBlocks = Object.values(values);

      // Check if there are any overlaps in the simple builder values
      const overlaps = getOverlapCidrs(cidrBlocks || []);

      if (overlaps.length > 0) {
        overlaps.forEach(({ cidr1, cidr2 }) => {
          // We do not want to show errors if the relevant addresses are not apart of the assigned input
          if (cidr1 === value || cidr2 === value) {
            // if the user enters duplicate addresses
            // then we should only show one copy of the error message
            const errorMessage = Messages.validation.addressOverlap(value, cidr1 !== value ? cidr1 : cidr2);
            const existingError = errors.find(error => error === errorMessage);

            if (!existingError) {
              errors.push(Messages.validation.addressOverlap(value, cidr1 !== value ? cidr1 : cidr2));
            }
          }
        });
      }
    }

    return errors.length > 0 ? errors : undefined;
  };

  const requiredValidator = (simpleBuilderValues: Record<string, string>[]): string[] | undefined => {
    const values = simpleBuilderValues
      ?.map(value => value[Fields.Address])
      .filter(value => !!value);

    if (!values || values.length < 1) {
      return [Messages.validation.required()];
    }

    return undefined;
  };

  const internalValidator = (simpleBuilderValues: Record<string, string>[]): string[] | undefined => {
    const errors: string[] = [];

    if (required) {
      errors.push(...(requiredValidator(simpleBuilderValues) || []));
    }

    errors.push(...(validator?.(simpleBuilderValues) || []));

    return errors.length > 0 ? errors : undefined;
  };

  return (
    <FieldWrapper
      label={label || Messages.labels.addressRange()}
      tooltip={FormattedString(
        {
          inputText:
            Messages.hints.toolTipAddressSpace(
              minPrefix.toString(),
              maxPrefix.toString(),
              networkOverviewUrl,
            ),
        },
      ) as unknown as string}
      required
    >
      <SimpleBuilder
        defaultValue={defaultValue}
        fieldName={fieldName}
        maxRows={maxRows}
        minRows={minRows}
        defaultNumberOfRows={defaultNumberOfRows}
        validator={internalValidator}
      >
        <OciCidr
          disabled={disabled}
          groupName={groupName}
          label={Messages.labels.cidr()}
          fieldName={Fields.Address}
          excludeCidrs={excludeCidrs}
          reservedCidrs={reservedCidrs}
          minPrefix={minPrefix}
          maxPrefix={maxPrefix}
          validator={overlappingAddressesValidator}
        />
      </SimpleBuilder>
    </FieldWrapper>
  );
};
